import React, { useEffect, useState } from "react";
import TextField from "./TextField";
import { Stack, Typography } from "@mui/material";

interface BirthdayFieldProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
}

const BirthdayField: React.FC<BirthdayFieldProps> = ({
    value,
    onChange,
    onBlur,
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [error, setError] = useState<string | null>(null);

    // Synchronize inputValue with value prop
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/[^0-9.]/g, ""); // Allow only numbers and "."
        let [day, month] = val.split(".");

        // Handle backspace
        if (
            e.nativeEvent instanceof InputEvent &&
            e.nativeEvent.inputType === "deleteContentBackward"
        ) {
            setInputValue(val);
            onChange(val);
            setError(null); // Clear error on input
            return;
        }

        // Day Validation: Restrict to 1–31
        if (day && (parseInt(day, 10) > 31 || day === "00")) {
            return;
        }

        // Automatic "." after 2 digits for day
        if (day?.length === 2 && !month) {
            val = day + ".";
        }

        // Month Validation: Restrict to 1–12
        if (month) {
            if (parseInt(month, 10) > 12 || month === "00") {
                return;
            }
            val = `${day}.${month}`;
        }

        setInputValue(val);
        onChange(val);
        setError(null);
    };

    const handleBlur = () => {
        // If the input is empty, treat it as valid and clear any errors
        if (!inputValue.trim()) {
            setError(null);
            if (onBlur) onBlur();
            return;
        }
        let [day, month] = inputValue.split(".");

        // Ensure both day and month are valid on blur
        day = day && parseInt(day, 10) > 0 ? day?.padStart(2, "0") : "";
        month = month && parseInt(month, 10) > 0 ? month.padStart(2, "0") : "";

        // If either day or month is invalid, show an error and don't submit
        if (!day || !month) {
            setError("Please enter a valid date in DD.MM format.");
            return;
        }

        const finalValue = `${day}.${month}`;

        setInputValue(finalValue);
        onChange(finalValue);
        setError(null); // Clear error on successful blur
        if (onBlur) onBlur();
    };

    return (
        <Stack spacing={1} width="100%" maxWidth="195px">
            <TextField
                variant="underlined"
                value={inputValue}
                onChange={handleInput}
                onBlur={handleBlur}
                placeholder="DD.MM"
                error={Boolean(error)} // Pass error state to TextField for styling
            />
            {error && (
                <Typography
                    style={{
                        color: "red",
                        fontSize: "12px",
                        lineHeight: "1.2", // Tighten spacing for multi-line text
                        whiteSpace: "normal", // Allow wrapping
                        wordBreak: "break-word", // Break long words if necessary
                    }}
                >
                    {error}
                </Typography>
            )}
        </Stack>
    );
};

export default BirthdayField;
